import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import { FileText } from 'lucide-react';
import { withPlainTextPaste } from '../../lib/quillPlainTextPaste';
import { filterMessageTemplates, MessageTemplateChoices, type MessageTemplateItem } from './MessageTemplateChoices';

const modules = withPlainTextPaste({ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'clean']] });

/** The inbox template catalogue, with the exact order context supplied by the caller. */
export function MessageTemplateEditor({ value, onChange, templates, onApply, busy }: {
  value: string;
  onChange: (html: string) => void;
  templates: MessageTemplateItem[];
  onApply: (template: MessageTemplateItem) => Promise<string>;
  busy: boolean;
}) {
  const editorRef = useRef<ReactQuill>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const templateButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ query?: string; start?: number; length?: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 320 });
  const [error, setError] = useState('');
  const filtered = menu?.query !== undefined ? filterMessageTemplates(templates, menu.query) : [];
  const applyRef = useRef<(template: MessageTemplateItem) => void>(() => {});
  applyRef.current = async template => {
    if (busy) return;
    const command = menu;
    setError('');
    try {
      const html = DOMPurify.sanitize(await onApply(template));
      const editor = editorRef.current?.getEditor();
      if (editor && command?.start !== undefined && command.length !== undefined) {
        // Never replace unrelated text if the selection changed during the request.
        if (editor.getText(command.start, command.length) !== `/${command.query}`) {
          setError('Treść zmieniła się. Wybierz szablon ponownie.');
          return;
        }
        editor.deleteText(command.start, command.length, 'api');
        editor.clipboard.dangerouslyPasteHTML(command.start, html, 'api');
        onChange(editor.root.innerHTML);
      } else {
        onChange(html);
      }
      setMenu(null);
      editor?.focus();
    } catch {
      setError('Nie udało się wstawić szablonu. Spróbuj ponownie.');
    }
  };

  useEffect(() => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    let frame = 0;
    const inspect = (_change?: unknown, _old?: unknown, source?: string) => {
      if (source !== 'user') return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const selection = editor.getSelection();
        if (!selection) return;
        const before = editor.getText(0, selection.index);
        const match = before.match(/(?:^|\s)\/([\p{L}\p{N}_-]*)$/u);
        if (match) {
          setMenu({ query: match[1], start: selection.index - match[1].length - 1, length: match[1].length + 1 });
          setActiveIndex(0);
        } else setMenu(null);
      });
    };
    editor.on('text-change', inspect);
    editor.on('selection-change', inspect);
    return () => { cancelAnimationFrame(frame); editor.off('text-change', inspect); editor.off('selection-change', inspect); };
  }, []);

  useLayoutEffect(() => {
    if (!menu) return;
    const place = () => {
      const rect = (menu.query === undefined ? templateButtonRef.current : editorRef.current?.getEditor().root)?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(360, window.innerWidth - 24);
      const height = menuRef.current?.offsetHeight || 300;
      const left = menu.query === undefined ? rect.right - width : rect.left;
      setPosition({ width, left: Math.max(12, Math.min(left, window.innerWidth - width - 12)), top: Math.max(12, Math.min(rect.top - height - 8, window.innerHeight - height - 12)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const outside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node) && !anchorRef.current?.contains(event.target as Node)) setMenu(null);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation(); setMenu(null); return;
      }
      if (menu.query === undefined || busy || !['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(event.key)) return;
      if (!filtered.length) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (event.key === 'ArrowDown') setActiveIndex(index => Math.min(index + 1, filtered.length - 1));
      else if (event.key === 'ArrowUp') setActiveIndex(index => Math.max(index - 1, 0));
      else if (filtered[activeIndex]) applyRef.current(filtered[activeIndex]);
    };
    document.addEventListener('mousedown', outside, true);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', outside, true); document.removeEventListener('keydown', key, true); };
  }, [menu, filtered, activeIndex, busy]);

  return <div ref={anchorRef} className="space-y-2">
    <ReactQuill ref={editorRef} className="inbox-editor rounded-xl overflow-hidden border border-border" modules={modules} theme="snow" value={value} onChange={onChange} readOnly={busy} placeholder="Napisz odpowiedź lub wpisz /, aby wybrać szablon..." />
    <div className="flex items-center justify-end gap-2">
      <span className="text-xs text-text-muted">/ wybór szablonu</span>
      <button ref={templateButtonRef} type="button" aria-label="Szablony wiadomości" aria-expanded={Boolean(menu)} disabled={busy} onClick={() => setMenu(menu ? null : {})} className="p-2 rounded-lg text-text-muted hover:text-accent-primary hover:bg-accent-primary/10 transition-all"><FileText className="w-4 h-4" /></button>
    </div>
    {error && <p role="alert" className="text-xs text-danger">{error}</p>}
    {menu && createPortal(<div ref={menuRef} style={{ ...position, zIndex: 12100 }} className="fixed max-h-[300px] overflow-y-auto bg-surface-raised border border-border rounded-xl shadow-2xl" onMouseDown={event => event.preventDefault()}>
      <MessageTemplateChoices templates={templates} query={menu.query} activeIndex={menu.query === undefined ? undefined : activeIndex} onSelect={template => applyRef.current(template)} disabled={busy} />
    </div>, document.body)}
  </div>;
}
